<template>
  <div class="login-page min-vh-100 d-flex align-items-center justify-content-center bg-light">
    <div class="login-container bg-white p-4 rounded shadow-sm" style="width: 100%; max-width: 400px">
      <div class="text-center mb-4">
        <h2 class="h3 mb-3">5 OF 5</h2>
        <p class="text-muted">Sign in to your account</p>
      </div>

      <!-- Phone Number Form -->
      <form v-if="!showOtpForm" @submit.prevent="requestOTP" class="needs-validation" novalidate>
        <div class="mb-4">
          <label for="phone" class="form-label">Phone Number</label>
          <vue-phone-number-input
            v-model="phone"
            :default-country-code="'IN'"
            :preferred-countries="['IN', 'US', 'GB']"
            :only-countries="[]"
            :color="'#0d6efd'"
            :border-radius="4"
            :valid-color="'#198754'" 
            :error-color="'#dc3545'"
            @update="onPhoneInput" :translations="{
              countrySelectorLabel: 'Country code',
              phoneNumberLabel: 'Phone number',
              example: 'Example:',
            }" 
            @input="removeError('phone')"
            :international="true" 
            :enabled-country-code="true"
            :enabled-flags="true"
            :auto-format="true"
            :default-format="'international'"
            :format="'international'" 
            :class="[{'is-invalid':formErrors.has('phone')}]"
            />
            <p class="text-danger" v-if="formErrors.has('phone')">
              {{ formErrors.first('phone') }}
            </p>
        </div>
        <VueRecaptcha ref="recaptcha" :sitekey="recaptchaSiteKey" @verify="onCaptchaVerified"
          @expired="onCaptchaExpired" class="captcha-div"/>
        <div v-if="captchaError" class="text-danger mt-2">{{ captchaError }}</div>
        <button type="submit" class="btn btn-primary w-100 py-2" :disabled="isLoading || !isValidPhone">
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
          {{ isLoading ? "Requesting OTP..." : "Request OTP" }}
        </button>
      </form>

      <!-- OTP Form -->
      <form v-else @submit.prevent="verifyOTP" class="needs-validation" novalidate>
        <div class="mb-4">
          <label for="otp" class="form-label">Enter OTP</label>
          <div class="input-group">
            <span class="input-group-text">
              <i class="bi bi-key"></i>
            </span>
            <input type="text" class="form-control" id="otp" v-model="otp" placeholder="Enter OTP" required />
          </div>
        </div>

        <button type="submit" class="btn btn-primary w-100 py-2" :disabled="isLoading">
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
          {{ isLoading ? "Verifying..." : "Verify OTP" }}
        </button>

        <button type="button" class="btn btn-link w-100 mt-2" @click="resetToPhoneInput" :disabled="isLoading">
          Change Phone Number
        </button>
      </form>

      <!-- Error Message -->
      <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
        {{ errorMessage }}
      </div>

      <div class="mt-4 flex flex-col items-center">
        <!-- Google Login Button -->
        <button
          @click="handleGoogleLogin"
          class="btn btn-outline-secondary w-100 fw-bold"
          :disabled="googleLoading"
        >
          <span v-if="googleLoading" class="spinner-border spinner-border-sm me-2"></span>
          {{ googleLoading ? "Connecting to Google..." : "Continue with Google" }}
        </button>
      </div>

      <div class="mt-3 text-center">
        Don't have an account?
        <router-link to="/signup">Sign up</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import {
  setStorage,
} from "@/Util/auth";
import { VueRecaptcha } from 'vue-recaptcha';
import googleAuthService from "@/services/googleAuthService";
import Error from "@/Util/Error";
export default {
  name: "AppLogin",
  components: {
    VueRecaptcha,
  },
  data() {
    return {
      phone: "",
      phoneno: "",
      otp: "",
      showOtpForm: false,
      isLoading: false,
      errorMessage: "",
      isValidPhone: false,
      countryCode: "IN",
      captchaResponse: null,
      captchaError: null,
      recaptchaSiteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
      googleLoading: false,
      formErrors: new Error({})
    };
  },
  mounted() {
    // Load Google Identity Services SDK
    if (!window.google) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
    window.onGoogleSignIn = this.onGoogleSignIn;
  },
  methods: {
    resetToPhoneInput() {
      this.showOtpForm = false;
      this.otp = "";
      this.errorMessage = "";
      this.captchaResponse = null;
      this.captchaError = null;
      if (this.$refs.recaptcha) {
        this.$refs.recaptcha.reset();
      }
    },
    onPhoneInput({ isValid, countryCode, formatInternational }) {
      if(formatInternational !== undefined){
        this.phone = formatInternational.replace(/\s+/g, "");
        this.phoneno = formatInternational.replace(/\s+/g, "");
      }
      this.isValidPhone = isValid;
      this.countryCode = countryCode;
    },
    onCaptchaVerified(response) {
      this.captchaResponse = response;
      this.captchaError = null;
    },
    onCaptchaExpired() {
      this.captchaResponse = null;
      this.captchaError = "Captcha expired. Please try again.";
    },
    async requestOTP() {
      if (!this.isValidPhone) {
        this.errorMessage = "Please enter a valid phone number";
        return;
      }
      if (!this.captchaResponse) {
        this.captchaError = "Please complete the captcha.";
        return;
      }
      this.isLoading = true;
      this.errorMessage = "";

      try {
        const response = await request({
          method: "post",
          url: `/auth/login`,
          data: {
            phone: this.phoneno,
          },
        });
        if (response.data.otp) {
          this.showOtpForm = true;
        }
      } catch (error) {
        if(error?.data?.errors){
          Object.assign(this.formErrors, new Error(error.data.errors));
        }else{
          this.errorMessage = error.data?.message || "Failed to request OTP";
        }
      } finally {
        this.isLoading = false;
        this.$refs.recaptcha.reset();
      }
    },

    async verifyOTP() {
      this.isLoading = true;
      this.errorMessage = "";

      try {
        const response = await request({
          method: "post",
          url: `/auth/verify-otp`,
          data: {
            otp: this.otp,
            phone: this.phoneno,
          },
        });

        if (response.data.token) {
          const authData = {
            token: response.data.token,
            user: response.data
          };
          setStorage('auth', JSON.stringify(authData));
          this.$store.dispatch("user",response.data);
          let redirectTo = null
          // Refresh the page to update the layout
          if(['super_admin', 'admin', 'brand_admin', 'account_admin'].includes(response.data.role)){
            redirectTo  = {name:'AdminDashboard'};
          }else if(response.data.role === 'customer'){
            redirectTo  = {name:'UserHome'};
          }else{
            redirectTo  = {name:'StaffDashboard'};
          }
          this.$router.push(this.$route.query.redirect || redirectTo).catch(err => {
            // if (err.name !== "Error" && !err.message.includes("Redirected")) {
              console.log(err);
            // }
          });
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || "Failed to verify OTP";
      } finally {
        this.isLoading = false;
      }
    },
    async handleGoogleLogin() {
      this.errors = {};
      this.googleLoading = true;
      try {
        if(this.$route.query && this.$route.query?.redirect){
          const redirectTo  = this.$route.query?.redirect?encodeURIComponent(this.$route.query?.redirect):'';
          await googleAuthService.redirectToGoogle(redirectTo);
        }else{
          googleAuthService.redirectToGoogle();
        }
      } catch (error) {
        this.googleLoading = false;
        this.errors.general = "Google login failed. Please try again.";
        console.error("Google login error:", error);
      }
    },
    removeError(target){
      this.formErrors.remove(target);
    }
  },
};
</script>

<style scoped>
.login-page {
  background-color: #f8f9fa;
}

.login-container {
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.input-group-text {
  background-color: transparent;
  border-right: none;
}

.input-group .form-control {
  border-left: none;
}

.min-vh-100 {
  min-height: 90vh !important;
}

.input-group .form-control:focus {
  border-color: #ced4da;
  box-shadow: none;
}

.input-group:focus-within {
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  border-radius: 0.375rem;
}

.btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn-primary:hover {
  background-color: #0b5ed7;
  border-color: #0a58ca;
}

.btn-link {
  color: #6c757d;
  text-decoration: none;
}

.btn-link:hover {
  color: #0d6efd;
}
::v-deep(.vue-phone-number-input.is-invalid input:not([readonly]) ) {
  border-color: var(--bs-form-invalid-border-color);
  padding-right: calc(1.5em + 0.75rem);
  background-image: url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 12 12%27 width=%2712%27 height=%2712%27 fill=%27none%27 stroke=%27%23dc3545%27%3e%3ccircle cx=%276%27 cy=%276%27 r=%274.5%27/%3e%3cpath stroke-linejoin=%27round%27 d=%27M5.8 3.6h.4L6 6.5z%27/%3e%3ccircle cx=%276%27 cy=%278.2%27 r=%27.6%27 fill=%27%23dc3545%27 stroke=%27none%27/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}
</style>
